/**
 * AgentInboxPage - the agent / system_admin ticket inbox (SPEC §9).
 *
 * Layout (top to bottom):
 *   - Header row: inbox icon + h1 "Tickets" + counts subtitle on the
 *     left; right-aligned search box (magnifying-glass icon, clear-x
 *     when populated).
 *   - Filter bar: five dropdowns - Status / Assignee / Priority / Type
 *     / Tag.
 *   - Table: # / Status / Priority / Contact / Company / Subject /
 *     Assignee / Updated. Row click navigates to /tickets/{number}.
 *
 * Filter state lives in component state and is carried by TWO
 * persistence layers with different jobs (SPEC sec 9, 2026-08-12):
 *   - The URL query string is the PER-TAB truth. Every state change
 *     mirrors into the URL (replace, no history spam), so a reload,
 *     a bookmark, or Back off a ticket detail reconstructs exactly
 *     this tab's view - independent of what other tabs did.
 *   - The server-side /me/preferences blob (slot "agent_inbox.filters")
 *     is the CROSS-SESSION default: it seeds fresh arrivals that carry
 *     no filter params (sidebar click, new tab, next morning) and
 *     still receives a debounced PUT on every edit.
 * Before 2026-08-12 the prefs blob was the only layer, which made the
 * last tab to write win globally - reloading an older tab snapped it
 * to the newest tab's filters (Sergey's multi-tab report).
 *
 * The free-text input is debounced 300ms with a 2-char floor
 * matching the backend Query(min_length=2).
 *
 * Status / type / priority option lists are sourced from the SPEC §9
 * filter bar definition.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Select, Spinner } from "@/components/ui";
import { useClearSearchSignal } from "@/hooks/useClearSearchSignal";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAgentFilteredTicketCount } from "@/hooks/useAgentFilteredTicketCount";
import { useAgentTicketCounts } from "@/hooks/useAgentTicketCounts";
import { useAgentTicketSearch } from "@/hooks/useAgentTicketSearch";
import { useAgentTickets, type ListSortBy, type SortOrder } from "@/hooks/useAgentTickets";
import { useAgents } from "@/hooks/useAgents";
import { useAgentTags } from "@/hooks/useTicketTags";
import {
  getPreferenceSlot,
  setPreferenceSlot,
  useSaveUserPreferences,
  useUserPreferences,
} from "@/hooks/useUserPreferences";
import type {
  SearchResultResponse,
  TicketPriority,
  TicketStatus,
  TicketSummary,
  TicketType,
} from "@/types/api";

import { PriorityBadge } from "@/components/tickets/PriorityBadge";
import { StatusBadge } from "@/components/tickets/StatusBadge";

// Legacy per-page localStorage key. Kept ONLY to migrate users who
// had a saved filter from before the server-backed preferences
// landed. ``migrateLegacyFiltersIfPresent`` consumes (deletes) the
// key on first read so subsequent loads ignore it. Server-side
// preferences (``useUserPreferences``) are now the source of truth.
const LEGACY_LOCAL_STORAGE_KEY = "buyist-support:agent-filters";
// Slot key inside the /me/preferences blob.
const PREF_SLOT_KEY = "agent_inbox.filters";

// Status filter options. Empty-string `value` represents the default
// "All" view (no `?status=` parameter sent). The Select component coerces
// option values to strings so the `null` sentinel lives only inside
// component state, not in the option list.
// Sentinel value for the Deleted view. The dropdown carries this
// like a normal status option; the page state translates it into
// ``deletedOnly=true`` + ``status=null`` before calling the API.
// "Deleted" is a soft-delete-row view, not a TicketStatus, hence
// the underscore-fenced sentinel rather than reusing the enum.
const DELETED_SENTINEL = "__deleted__";
// "All Open & Unassigned" preset (added 2026-05-20 after Greg
// discovered the Reopened-status filter and found tickets that had
// been missed). Sentinel that, when chosen, flips the API request
// to ``?view=open_or_unassigned`` and clears the single-status
// filter. Backend applies ``status IN (open, reopened) AND
// (assignee IS NULL OR assignee IN tracked-team)``.
const OPEN_OR_UNASSIGNED_SENTINEL = "__open_or_unassigned__";
// "All My Open & Unassigned" preset (added 2026-05-26). Personal
// counterpart to the team preset: same status restriction, but the
// assignee arm narrows to "me or unassigned" instead of "the team
// or unassigned". Backend applies ``status IN (open, reopened) AND
// (assignee IS NULL OR assignee = me)``. Sits ABOVE the team preset
// in the dropdown so the personal queue is the first option.
const MINE_OR_UNASSIGNED_SENTINEL = "__mine_or_unassigned__";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: MINE_OR_UNASSIGNED_SENTINEL, label: "All My Open & Unassigned" },
  { value: OPEN_OR_UNASSIGNED_SENTINEL, label: "All Open & Unassigned" },
  { value: "", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "pending", label: "Pending" },
  { value: "waiting_customer", label: "Waiting on Customer" },
  { value: "waiting_third_party", label: "Waiting on Third Party" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "reopened", label: "Reopened" },
  { value: "spam", label: "Spam" },
  { value: DELETED_SENTINEL, label: "Deleted" },
];

const PRIORITY_OPTIONS: { value: "" | TicketPriority; label: string }[] = [
  { value: "", label: "All priorities" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Real types sorted alphabetically; the "All types" sentinel stays first.
const TYPE_OPTIONS: { value: "" | TicketType; label: string }[] = [
  { value: "", label: "All types" },
  { value: "DevOps Task", label: "DevOps Task" },
  { value: "Failed Credit Card Charge", label: "Failed Credit Card Charge" },
  { value: "Internal Task", label: "Internal Task" },
  { value: "Platform Development", label: "Platform Development" },
  { value: "Problem", label: "Problem" },
  { value: "Question", label: "Question" },
  { value: "Service Request", label: "Service Request" },
  { value: "Vendor Support", label: "Vendor Support" },
];

// Static rows always at the top of the assignee filter. Real agent
// rows from /agent/agents (via useAgents) get appended below this
// list at render time so the dropdown matches the ticket-detail
// AssigneePicker. Backend filter at GET /agent/tickets accepts
// "mine" / "unassigned" / "all" / a user UUID.
const STATIC_ASSIGNEE_OPTIONS = [
  { value: "all", label: "All assignees" },
  { value: "mine", label: "Mine" },
  { value: "unassigned", label: "Unassigned" },
];

interface PersistedFilters {
  status: TicketStatus | null;
  assignee: string;
  priority: TicketPriority | "";
  type: TicketType | "";
  /** Tag NAME filter (2026-07-13). "" = All tags. Persisted as the
   * name (not the UUID) so the prefs blob stays human-readable and
   * environment-agnostic; a stale name safely yields zero rows. */
  tag: string;
  q: string;
  /** Column the user clicked to sort by. Persisted alongside the
   * other filter selections so the inbox remembers it across sessions
   * per Greg's request. */
  sort_by: ListSortBy;
  /** Direction of the active sort. */
  sort_order: SortOrder;
  /** Soft-delete view. Mutually exclusive with ``status`` - when this
   * is true the API call sends ``deleted_only=true`` and no status
   * param, so deleted rows of every status come back. The status
   * dropdown shows the DELETED_SENTINEL option as the current value. */
  deletedOnly: boolean;
  /** "All Open & Unassigned" preset (added 2026-05-20). Mutually
   * exclusive with ``status`` and with ``deletedOnly``. When true,
   * the API call sends ``view=open_or_unassigned`` and no status
   * param. The status dropdown shows the OPEN_OR_UNASSIGNED_SENTINEL
   * option as the current value. */
  openOrUnassigned: boolean;
  /** "All My Open & Unassigned" preset (added 2026-05-26). Mutually
   * exclusive with the other two sentinels. When true, the API call
   * sends ``view=mine_or_unassigned`` and the status dropdown
   * displays the MINE_OR_UNASSIGNED_SENTINEL value. */
  mineOrUnassigned: boolean;
}

// SPEC sec 9: the agent inbox default is "Open only" - agents
// triaging the queue want to see work that's actively pending. The
// "All" pill is one click away for cases that need history.
const DEFAULT_FILTERS: PersistedFilters = {
  status: "open",
  assignee: "all",
  priority: "",
  type: "",
  tag: "",
  q: "",
  sort_by: "last_activity_at",
  sort_order: "desc",
  deletedOnly: false,
  openOrUnassigned: false,
  mineOrUnassigned: false,
};

// ---------------------------------------------------------------------
// URL <-> filter-state serialization (2026-08-12, Sergey's multi-tab
// report). The URL is the per-tab source of truth; the prefs blob only
// seeds arrivals that carry no filter params. Exported for tests.
// ---------------------------------------------------------------------

/** Recognized non-q filter param keys. Presence of ANY of these puts
 * the mount into URL mode (state parsed from the URL, prefs ignored).
 * ``q`` is deliberately excluded: a bare ``?q=`` link is the existing
 * cross-page-search contract, which seeds the search box but keeps the
 * dropdown filters coming from prefs. */
const URL_FILTER_KEYS = [
  "view",
  "deleted",
  "status",
  "assignee",
  "priority",
  "type",
  "tag",
  "sort_by",
  "sort_order",
] as const;

// Concrete enum vocabularies for defensive URL parsing. A hand-edited
// or stale URL must degrade to that field's default, never crash the
// page or 422 the backend.
const VALID_STATUS_VALUES = new Set<string>(
  STATUS_OPTIONS.map((o) => o.value).filter(
    (v) =>
      v !== "" &&
      v !== DELETED_SENTINEL &&
      v !== OPEN_OR_UNASSIGNED_SENTINEL &&
      v !== MINE_OR_UNASSIGNED_SENTINEL,
  ),
);
const VALID_PRIORITY_VALUES = new Set<string>(
  PRIORITY_OPTIONS.map((o) => o.value).filter((v) => v !== ""),
);
const VALID_TYPE_VALUES = new Set<string>(
  TYPE_OPTIONS.map((o) => o.value).filter((v) => v !== ""),
);
const VALID_SORT_BY_VALUES = new Set<string>([
  "ticket_number",
  "status",
  "priority",
  "requester_name",
  "subject",
  "assignee_name",
  "company_name",
  "last_activity_at",
]);

export function hasRecognizedFilterParams(params: URLSearchParams): boolean {
  return URL_FILTER_KEYS.some((key) => params.has(key));
}

/** Serialize filter state into URL query params. Exactly one
 * status-family param is ALWAYS present (``view`` / ``deleted`` /
 * ``status``, with ``status=all`` standing in for the null "All
 * statuses" value) so a mirrored URL is never bare - that guarantees a
 * reload of this tab re-enters URL mode instead of re-seeding from the
 * shared prefs blob. Everything else is included only when it differs
 * from the default, keeping shared URLs short. */
export function serializeFiltersToParams(
  filters: PersistedFilters,
): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.mineOrUnassigned) {
    params.set("view", "mine_or_unassigned");
  } else if (filters.openOrUnassigned) {
    params.set("view", "open_or_unassigned");
  } else if (filters.deletedOnly) {
    params.set("deleted", "1");
  } else {
    params.set("status", filters.status ?? "all");
  }
  if (filters.assignee !== DEFAULT_FILTERS.assignee) {
    params.set("assignee", filters.assignee);
  }
  if (filters.priority !== DEFAULT_FILTERS.priority) {
    params.set("priority", filters.priority);
  }
  if (filters.type !== DEFAULT_FILTERS.type) {
    params.set("type", filters.type);
  }
  if (filters.tag !== DEFAULT_FILTERS.tag) {
    params.set("tag", filters.tag);
  }
  if (filters.q !== DEFAULT_FILTERS.q) {
    params.set("q", filters.q);
  }
  // Sort params travel as a pair whenever either differs from the
  // default, so a shared URL is unambiguous about both.
  if (
    filters.sort_by !== DEFAULT_FILTERS.sort_by ||
    filters.sort_order !== DEFAULT_FILTERS.sort_order
  ) {
    params.set("sort_by", filters.sort_by);
    params.set("sort_order", filters.sort_order);
  }
  return params;
}

/** Parse a URL's query params into a full ``PersistedFilters``.
 * Missing fields fall back to ``DEFAULT_FILTERS`` (NOT to prefs) so a
 * partial hand-written or bookmarked URL is deterministic for whoever
 * opens it. Enum-typed fields are validated against the option lists;
 * free-string fields (tag, q, assignee) pass through - a stale tag
 * name safely yields zero rows, same as the prefs path. Conflicting
 * status-family params resolve in the serializer's precedence order
 * (view, then deleted, then status). */
export function parseFiltersFromParams(
  params: URLSearchParams,
): PersistedFilters {
  const view = params.get("view");
  const mineOrUnassigned = view === "mine_or_unassigned";
  const openOrUnassigned = view === "open_or_unassigned";
  const deletedOnly =
    !mineOrUnassigned && !openOrUnassigned && params.get("deleted") === "1";
  let status: TicketStatus | null = DEFAULT_FILTERS.status;
  if (mineOrUnassigned || openOrUnassigned || deletedOnly) {
    status = null;
  } else {
    const rawStatus = params.get("status");
    if (rawStatus === "all") {
      status = null;
    } else if (rawStatus && VALID_STATUS_VALUES.has(rawStatus)) {
      status = rawStatus as TicketStatus;
    }
  }
  const rawPriority = params.get("priority");
  const rawType = params.get("type");
  const rawSortBy = params.get("sort_by");
  const rawSortOrder = params.get("sort_order");
  return {
    status,
    assignee: params.get("assignee") || DEFAULT_FILTERS.assignee,
    priority:
      rawPriority && VALID_PRIORITY_VALUES.has(rawPriority)
        ? (rawPriority as TicketPriority)
        : DEFAULT_FILTERS.priority,
    type:
      rawType && VALID_TYPE_VALUES.has(rawType)
        ? (rawType as TicketType)
        : DEFAULT_FILTERS.type,
    tag: params.get("tag") ?? DEFAULT_FILTERS.tag,
    q: params.get("q") ?? DEFAULT_FILTERS.q,
    sort_by:
      rawSortBy && VALID_SORT_BY_VALUES.has(rawSortBy)
        ? (rawSortBy as ListSortBy)
        : DEFAULT_FILTERS.sort_by,
    sort_order:
      rawSortOrder === "asc" || rawSortOrder === "desc"
        ? rawSortOrder
        : DEFAULT_FILTERS.sort_order,
    deletedOnly,
    openOrUnassigned,
    mineOrUnassigned,
  };
}

/** Normalize a partial blob into a full ``PersistedFilters`` by
 * filling in defaults. Used for both server-side and legacy-local
 * shapes - either side may be missing keys after a schema change.
 *
 * IMPORTANT: ``status`` accepts ``null`` as a legitimate "All
 * statuses" value, distinct from "key absent, use default". Other
 * string-typed fields use ``""`` for their "All" sentinel so the
 * ``??`` coalesce works there; for status we check key presence
 * explicitly. Earlier this used ``??`` for status too which silently
 * coerced ``null`` back to the ``"open"`` default - the user saw
 * "All statuses doesn't take effect until I click it twice".
 */
function fillFilterDefaults(
  parsed: Partial<PersistedFilters> | null | undefined,
): PersistedFilters {
  if (!parsed) return DEFAULT_FILTERS;
  const rawSortBy = parsed.sort_by as string | undefined;
  // Migrate old localStorage values: "updated_at" was the previous
  // sort key for the "Last Activity" column. Translate at read time
  // so the next write cleans up the shape.
  const migratedSortBy: ListSortBy | undefined =
    rawSortBy === "updated_at"
      ? "last_activity_at"
      : (rawSortBy as ListSortBy | undefined);
  const hasStatusKey = "status" in parsed;
  return {
    status: hasStatusKey
      ? ((parsed.status as TicketStatus | null) ?? null)
      : DEFAULT_FILTERS.status,
    assignee: parsed.assignee ?? DEFAULT_FILTERS.assignee,
    priority: (parsed.priority as TicketPriority | "") ?? DEFAULT_FILTERS.priority,
    type: (parsed.type as TicketType | "") ?? DEFAULT_FILTERS.type,
    tag: parsed.tag ?? DEFAULT_FILTERS.tag,
    q: parsed.q ?? DEFAULT_FILTERS.q,
    sort_by: migratedSortBy ?? DEFAULT_FILTERS.sort_by,
    sort_order: (parsed.sort_order as SortOrder) ?? DEFAULT_FILTERS.sort_order,
    deletedOnly: parsed.deletedOnly ?? DEFAULT_FILTERS.deletedOnly,
    openOrUnassigned: parsed.openOrUnassigned ?? DEFAULT_FILTERS.openOrUnassigned,
    mineOrUnassigned: parsed.mineOrUnassigned ?? DEFAULT_FILTERS.mineOrUnassigned,
  };
}

/** One-time read of the legacy page-local localStorage key so users
 * who already saved filters before server-backed prefs landed keep
 * their choices. The migration consumes (deletes) the legacy key on
 * first read so subsequent loads ignore it.
 */
function readLegacyLocalFilters(): Partial<PersistedFilters> | null {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Partial<PersistedFilters>;
  } catch {
    return null;
  }
}

function consumeLegacyLocalFilters(): void {
  try {
    localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
  } catch {
    /* noop */
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

function shortId(value: string | null): string {
  if (!value) return "-";
  // The full UUID is heavy. Show the first 8 chars so agents can
  // visually distinguish rows without overwhelming the column. Real
  // names ship with the user / company list endpoints in Phase 12.
  return value.slice(0, 8);
}

/**
 * Common shape between TicketSummary (list endpoint) and
 * SearchResultResponse (search endpoint). The table renders from
 * either source by reading only these fields.
 */
interface InboxRow {
  id: string;
  ticket_number: number;
  /** Customer-facing short code (e.g. "JK4" or "F-12345"). Shown as
   * the primary identifier in the inbox row; ticket_number stays
   * around for the URL path and for sorting purposes. */
  public_code: string;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  requester_email: string;
  requester_name?: string;
  assignee_user_id: string | null;
  /** Joined from users.full_name on the backend; falls back to a UUID
   * slug only when the row is stale or the join missed. */
  assignee_name: string | null;
  company_id: string | null;
  /** Joined from companies.name on the backend. */
  company_name: string | null;
  /** MAX(comments.created_at) or ticket.created_at when no comments
   * exist. Drives the "Last Activity" column. Falls back to
   * updated_at on the rare row where the backend hasn't shipped the
   * field yet. */
  last_activity_at: string;
  /** Set only when the row comes from the search endpoint. */
  match_via?: SearchResultResponse["match_via"];
}

function fromSummary(t: TicketSummary): InboxRow {
  return {
    id: t.id,
    ticket_number: t.ticket_number,
    public_code: t.public_code,
    subject: t.subject,
    status: t.status,
    priority: t.priority,
    requester_email: t.requester_email,
    requester_name: t.requester_name,
    assignee_user_id: t.assignee_user_id,
    assignee_name: t.assignee_name ?? null,
    company_id: t.company_id,
    company_name: t.company_name ?? null,
    last_activity_at: t.last_activity_at ?? t.updated_at,
  };
}

function fromSearchHit(r: SearchResultResponse): InboxRow {
  return {
    id: r.ticket_id,
    ticket_number: r.ticket_number,
    public_code: r.public_code,
    subject: r.subject,
    status: r.status as TicketStatus,
    priority: r.priority as TicketPriority,
    requester_email: r.requester_email,
    requester_name: r.requester_name,
    assignee_user_id: r.assignee_user_id,
    assignee_name: r.assignee_name ?? null,
    company_id: r.company_id,
    company_name: r.company_name ?? null,
    last_activity_at: r.last_activity_at ?? r.updated_at,
    match_via: r.match_via,
  };
}

interface SortableThProps {
  column: ListSortBy;
  label: string;
  filters: PersistedFilters;
  onToggle: (col: ListSortBy) => void;
}

function SortableTh({ column, label, filters, onToggle }: SortableThProps): JSX.Element {
  const isActive = filters.sort_by === column;
  const arrow = !isActive ? "" : filters.sort_order === "desc" ? " ▼" : " ▲";
  return (
    <th className="px-3 py-2">
      <button
        type="button"
        onClick={() => onToggle(column)}
        className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wider ${
          isActive ? "text-primary-700" : "text-neutral-500 hover:text-neutral-700"
        }`}
        data-testid={`sort-th-${column}`}
        data-sort-active={isActive ? "true" : "false"}
        data-sort-order={isActive ? filters.sort_order : ""}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {arrow && <span aria-hidden="true">{arrow}</span>}
      </button>
    </th>
  );
}

function TicketTableRow({
  ticket,
}: {
  ticket: InboxRow;
}): JSX.Element {
  const navigate = useNavigate();
  return (
    <tr
      onClick={() => navigate(`/tickets/${ticket.public_code.toLowerCase()}`)}
      className="cursor-pointer hover:bg-neutral-50"
    >
      {/* ``data-label`` + the ``tickets-card-*`` classes drive the
          mobile card layout: on phones (< lg) styles/index.css turns
          each row into a card and renders these labels via ::before.
          On desktop the labels are inert (the table header shows the
          column names). */}
      <td className="tickets-card-code whitespace-nowrap px-3 py-3 text-xs font-medium text-neutral-700">
        {/* Show the customer-facing short code as the primary
            identifier (e.g. "JK4" or "F-12345"). The internal
            ticket_number stays in the URL path and still drives
            sorting; the data-testid keeps existing tests happy by
            being keyed on the visible label rather than on the
            internal number. ``whitespace-nowrap`` on the cell keeps
            F-prefixed codes ("F-104896") on a single line - the
            default behavior wraps after the hyphen when the column
            is narrow. */}
        <span data-testid="ticket-number" title={`#${ticket.ticket_number}`}>
          {ticket.public_code}
        </span>
      </td>
      <td className="px-3 py-3" data-label="Status">
        <StatusBadge status={ticket.status} />
      </td>
      <td className="px-3 py-3" data-label="Priority">
        <PriorityBadge priority={ticket.priority} />
      </td>
      <td className="px-3 py-3 text-sm text-neutral-700" data-label="Contact">
        {ticket.requester_name || ticket.requester_email}
      </td>
      <td className="px-3 py-3 text-xs text-neutral-600" data-label="Company">
        {ticket.company_name ?? (
          <span className="text-neutral-400" title={ticket.company_id ?? "no company"}>
            {ticket.company_id ? shortId(ticket.company_id) : "-"}
          </span>
        )}
      </td>
      {/* ``data-code`` lets the mobile card render the header as
          "{code} - {subject}" via a ::before on this cell (see
          styles/index.css), so the code cell itself can be hidden on
          phones without losing the identifier. Inert on desktop. */}
      <td
        className="tickets-card-subject px-3 py-3 text-sm text-neutral-900"
        data-code={ticket.public_code}
      >
        <span className="line-clamp-2">{ticket.subject}</span>
        {/* match_via was previously surfaced here as "(trgm)" / "(vector)"
         * / "(both)" - useful as a search-tuning aid during Phase 10
         * but noise for day-to-day triage. The value still ships on
         * the row for tests / future use and renders on the
         * RelatedTicketsRail where it has product value (explaining
         * WHY a neighbor is being suggested). */}
        {ticket.match_via && (
          <span
            data-testid="match-via"
            data-match-via={ticket.match_via}
            hidden
          />
        )}
      </td>
      <td className="px-3 py-3 text-xs text-neutral-600" data-label="Assignee">
        {ticket.assignee_name ?? (
          <span className="text-neutral-400" title={ticket.assignee_user_id ?? "unassigned"}>
            {ticket.assignee_user_id ? shortId(ticket.assignee_user_id) : "Unassigned"}
          </span>
        )}
      </td>
      <td className="px-3 py-3 text-xs text-neutral-500" data-label="Last activity">
        {formatRelative(ticket.last_activity_at)}
      </td>
    </tr>
  );
}

export function AgentInboxPage(): JSX.Element {
  usePageTitle("Tickets - Buyist Support");

  // Server-backed preferences. The hook returns the localStorage
  // warm-start blob (via placeholderData) so first paint shows the
  // user's saved filters with no API roundtrip. On a prefs-seeded
  // mount the first real server payload reconciles once (see the
  // one-shot latch below); on a URL-seeded mount prefs are read-only
  // context for the PUT and never applied to this tab's state.
  const prefsQuery = useUserPreferences();
  const prefsSaver = useSaveUserPreferences();

  // A ``?q=`` in the URL seeds the search box - this is how the search
  // box on the ticket detail page (and any other page) sends the agent
  // here with a query already running. Read once at mount.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get("q")?.trim() ?? "";

  // Whether this tab MOUNTED with filter params in the URL (URL mode).
  // Captured once at mount: the mirror effect below writes params on
  // every state change, so re-evaluating later would always say true.
  const mountedInUrlModeRef = useRef(hasRecognizedFilterParams(searchParams));

  // Seed local state. Three arrival shapes, in priority order:
  //   1. URL mode - any recognized filter param present: the query
  //      string is this tab's complete state (missing fields fall back
  //      to DEFAULT_FILTERS, not to prefs, so a shared URL is
  //      deterministic). Prefs are ignored for the whole mount, which
  //      is the multi-tab fix: another tab's writes cannot leak into
  //      this tab on reload.
  //   2. ``?q=`` only - the cross-page search link: q seeds the search
  //      box, the dropdown filters still come from prefs.
  //   3. Bare URL (sidebar click, fresh login): prefs seed everything,
  //      plus the one-time migration of the legacy localStorage key.
  //      ``consumeLegacyLocalFilters`` deletes that key after the
  //      migration runs so subsequent mounts see only the server value.
  const [filters, setFilters] = useState<PersistedFilters>(() => {
    if (mountedInUrlModeRef.current) {
      return parseFiltersFromParams(searchParams);
    }
    const legacy = readLegacyLocalFilters();
    const fromPrefs = getPreferenceSlot<Partial<PersistedFilters>>(
      prefsQuery.data,
      PREF_SLOT_KEY,
    );
    // Prefer server-side prefs over legacy local. The legacy file is
    // consumed unconditionally so it doesn't keep overriding fresh
    // server changes once the user has clicked something.
    if (legacy) consumeLegacyLocalFilters();
    const base = fillFilterDefaults(fromPrefs ?? legacy);
    // A URL ``?q=`` wins over the persisted search term so a cross-page
    // search link lands directly on its results.
    return urlQ ? { ...base, q: urlQ } : base;
  });

  // When the server query resolves AFTER initial render (placeholder
  // was empty / cache was stale), reconcile local state with the
  // authoritative server value - but ONLY if the user has not edited
  // the filters in the meantime. ``hasUserEditedRef`` flips on the
  // first manual change and locks out further server overwrites for
  // this session so the user's clicks don't get clobbered mid-edit.
  // Initialized true when the tab mounted in URL mode (the URL, not
  // the prefs blob, is this tab's truth) or arrived with a ``?q=``
  // link (the seeded search term must not be clobbered; filters are
  // disabled in search mode anyway, so skipping the saved filter set
  // for that one load is harmless).
  const hasUserEditedRef = useRef(
    mountedInUrlModeRef.current || Boolean(urlQ),
  );
  // One-shot latch: the reconcile applies the FIRST real (non-
  // placeholder) server payload, then locks. Before 2026-08-12 it
  // re-applied on every prefs refetch (stale-timer or window-focus),
  // which let another tab's newer blob silently swap an idle tab's
  // filters mid-session.
  const prefsReconciledRef = useRef(false);
  useEffect(() => {
    if (!prefsQuery.isSuccess || prefsQuery.isPlaceholderData) return;
    if (prefsReconciledRef.current) return;
    prefsReconciledRef.current = true;
    if (hasUserEditedRef.current) return;
    const fromPrefs = getPreferenceSlot<Partial<PersistedFilters>>(
      prefsQuery.data,
      PREF_SLOT_KEY,
    );
    if (fromPrefs) setFilters(fillFilterDefaults(fromPrefs));
  }, [prefsQuery.isSuccess, prefsQuery.isPlaceholderData, prefsQuery.data]);

  // Single wrapper for every user-driven filter change. Updates local
  // state AND fires the debounced PUT in the same call so we never
  // race the server-reconcile effect (which would otherwise echo the
  // user's click back to default after the next API tick).
  function updateFilters(
    updater: (prev: PersistedFilters) => PersistedFilters,
  ): void {
    hasUserEditedRef.current = true;
    setFilters((prev) => {
      const next = updater(prev);
      prefsSaver.saveDebounced(
        setPreferenceSlot(prefsQuery.data, PREF_SLOT_KEY, next),
      );
      return next;
    });
  }

  // Clicking Tickets in the sidebar clears the search box and leaves
  // everything else alone - identical end state to pressing the X, so
  // it goes through updateFilters and persists like any other edit.
  // Arriving any other way (Back off a ticket detail, a ?q= link, a
  // reload) keeps the search. See hooks/useClearSearchSignal.
  useClearSearchSignal(() => {
    if (!filters.q) return;
    updateFilters((f) => ({ ...f, q: "" }));
  });

  // Mobile-only: whether the docked Filters sheet is expanded. The
  // four filter dropdowns live in a bar pinned to the bottom of the
  // viewport on phones (< lg); the peek toggles the full set. Collapsed
  // by default. Inert on desktop where the same single filter bar
  // renders at the top via `lg:` resets (no duplicate controls).
  const [filtersDockOpen, setFiltersDockOpen] = useState(false);

  // Debounce the free-text search (SPEC §9 + 300ms in the brief).
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQ(filters.q);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [filters.q]);

  // The search endpoint accepts q with min_length=1; the legacy list
  // endpoint required min_length=2 for trigram-ILIKE sanity. We switch
  // to search mode when the trimmed query has any content so the row
  // count shrinks immediately on the first keystroke - this matches
  // the SPEC §9 "search-as-you-type" feel.
  const trimmedQ = debouncedQ.trim();
  const isSearchMode = trimmedQ.length > 0;

  // Mirror every filter-state change into the URL, making the query
  // string this tab's durable truth (reload / Back / bookmark all
  // reconstruct exactly this view). ``replace`` keeps history clean -
  // Back never steps through old filter combinations, matching the
  // customer MyTicketsPage's ?status= pattern. Declared AFTER
  // useClearSearchSignal so within one effects flush the sidebar-click
  // token is consumed before this rewrite replaces the history entry.
  // The string compare makes the effect a no-op once the URL matches,
  // so the setSearchParams -> searchParams-change -> effect cycle
  // terminates immediately.
  //
  // ``q`` mirrors from the DEBOUNCED value, not the per-keystroke one:
  // Safari rate-limits history.replaceState (~100 calls / 30s, then it
  // throws), so writing per keystroke could crash navigation for a
  // fast typist. One write per typing pause is also all the fidelity a
  // reload needs.
  useEffect(() => {
    const next = serializeFiltersToParams({ ...filters, q: debouncedQ });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, debouncedQ, searchParams, setSearchParams]);

  const counts = useAgentTicketCounts();
  // Filter-aware total used by the subtitle (added 2026-05-21).
  // Shares filter args with the list hook below so the displayed
  // count is always the true total for the active filter set
  // (modulo pagination on the list - the count is the TRUE total,
  // the list paginates at 50 rows).
  // Picks which compound preset (if any) is active. Both sentinels
  // are mutually exclusive so this collapses to one value.
  const activeView: "open_or_unassigned" | "mine_or_unassigned" | undefined =
    filters.mineOrUnassigned
      ? "mine_or_unassigned"
      : filters.openOrUnassigned
        ? "open_or_unassigned"
        : undefined;
  const presetActive = activeView !== undefined;
  const filteredCount = useAgentFilteredTicketCount({
    status: filters.deletedOnly || presetActive
      ? undefined
      : filters.status ?? undefined,
    assignee: filters.assignee || undefined,
    priority: filters.priority || undefined,
    type: filters.type || undefined,
    tag: filters.tag || undefined,
    deletedOnly: filters.deletedOnly,
    view: activeView,
  });
  // Staff directory for the assignee filter. Cached for the session
  // by the hook; degrades gracefully (just the three static options)
  // while the fetch is in flight or if it fails.
  const agents = useAgents();
  // In-use tag vocabulary for the Tag filter (2026-07-13). The server
  // returns rows alphabetical by lower(name) already, so the options
  // list just prepends the "" sentinel that means "no tag param".
  const agentTags = useAgentTags();
  const tagOptions = useMemo(
    () => [
      { value: "", label: "All tags" },
      ...(agentTags.data ?? []).map((t) => ({
        value: t.name,
        label: t.name,
      })),
    ],
    [agentTags.data],
  );
  const assigneeOptions = useMemo(() => {
    const directory = agents.data ?? [];
    if (directory.length === 0) return STATIC_ASSIGNEE_OPTIONS;
    return [
      ...STATIC_ASSIGNEE_OPTIONS,
      { value: "__sep__", label: "──────────" },
      ...directory.map((a) => ({
        value: a.id,
        label: a.full_name || a.email,
      })),
    ];
  }, [agents.data]);
  // The list query stays mounted (and keeps its 30s poll) even while
  // search mode renders from the search endpoint - there is no
  // ``enabled`` gate. The upside is a warm cache: clearing the search
  // box swaps the list back instantly instead of flashing a refetch
  // spinner. The cost is one background list request per poll tick
  // during a search.
  // When either preset (openOrUnassigned / mineOrUnassigned) is
  // active we let the backend ``view`` param own the status filter
  // (it contains an OR clause over status), but the assignee +
  // other dropdowns still flow through as AND constraints per
  // Greg's 2026-05-20 decision.
  const list = useAgentTickets({
    status: filters.deletedOnly || presetActive
      ? undefined
      : filters.status ?? undefined,
    assignee: filters.assignee || undefined,
    priority: filters.priority || undefined,
    type: filters.type || undefined,
    tag: filters.tag || undefined,
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
    deletedOnly: filters.deletedOnly,
    view: activeView,
  });
  // Search hook is disabled when q is empty. Filters (status / priority /
  // type / assignee) are deliberately NOT passed in search mode: when an
  // agent types a query they want the full ticket corpus searched, not
  // a search-within-the-current-filter. The dropdowns visibly disable
  // while searching so the inert state is obvious. Sort is preserved
  // since it is presentation, not filtering; the search endpoint
  // accepts the same sort_by values plus "score".
  const search = useAgentTicketSearch(trimmedQ, {
    sort_by: filters.sort_by,
    sort_order: filters.sort_order,
  }, 100);

  // Subtitle copy (2026-05-21 rewrite). The OLD subtitle showed two
  // fixed counts ("X open across all tickets, Y assigned to you")
  // regardless of which filter the agent had selected, which was
  // confusing when the visible rows were a small filtered subset.
  // The NEW subtitle is a single number that ALWAYS reflects the
  // active filter, driven by GET /agent/tickets/count which mirrors
  // the list endpoint's WHERE-clause logic.
  //
  // In search mode the displayed-count fallback is used because the
  // search endpoint doesn't return a total - we show "N matching"
  // with a "+" suffix when there's a next page, so the agent at
  // least knows there's more. ``counts.data`` is still loaded
  // because the existing useAgentTicketCounts query owns the
  // per-status numbers elsewhere; we just don't read them here.
  const subtitle = useMemo(() => {
    if (isSearchMode) {
      const hits = search.data?.pages ?? [];
      const total = hits.reduce(
        (sum, page) => sum + (page.data?.length ?? 0),
        0,
      );
      const hasMore = hits.length > 0 && hits[hits.length - 1]?.next_cursor != null;
      const plural = total === 1 ? "ticket" : "tickets";
      const suffix = hasMore ? "+" : "";
      if (search.isLoading) return "Searching...";
      return `${total}${suffix} matching ${plural}`;
    }
    if (filteredCount.data == null) return "Loading...";
    const total = filteredCount.data.total;
    const plural = total === 1 ? "ticket" : "tickets";
    return `${total} ${plural}`;
  }, [
    isSearchMode,
    search.data,
    search.isLoading,
    filteredCount.data,
  ]);
  // Silence the unused-binding lint when the old per-status counts
  // query stays mounted for the sidebar header strip. We still
  // keep the hook call above so other consumers of the cache (and
  // future header redesigns) don't have to rewire the fetch.
  void counts;

  function selectStatus(value: TicketStatus | null) {
    // Picking a regular status clears all special-mode sentinels so
    // the API request stops sending ``view=`` / ``deleted_only=``.
    updateFilters((f) => ({
      ...f,
      status: value,
      deletedOnly: false,
      openOrUnassigned: false,
      mineOrUnassigned: false,
    }));
  }

  function selectDeleted() {
    // Deleted is a top-of-dropdown view that ignores the status
    // filter (a deleted ticket can have any prior status). Clearing
    // it returns to "All statuses" rather than the last status the
    // user had picked - simpler model, no surprise reappearance.
    updateFilters((f) => ({
      ...f,
      status: null,
      deletedOnly: true,
      openOrUnassigned: false,
      mineOrUnassigned: false,
    }));
  }

  function selectOpenOrUnassigned() {
    // "All Open & Unassigned" preset: clear single-status + deleted
    // + mine sentinels so the API call sends
    // ``view=open_or_unassigned``. Per Greg's decision (2026-05-20),
    // priority / type / search / assignee keep their current values
    // and AND with the preset.
    updateFilters((f) => ({
      ...f,
      status: null,
      deletedOnly: false,
      openOrUnassigned: true,
      mineOrUnassigned: false,
    }));
  }

  function selectMineOrUnassigned() {
    // "All My Open & Unassigned" preset: clears every other
    // sentinel + the single-status filter. API call sends
    // ``view=mine_or_unassigned``. Unlike the team preset (which
    // ANDs with whatever the other dropdowns hold), this one ALSO
    // resets assignee / priority / type / tag to their "All"
    // sentinels - per Greg's request (2026-06-11) the personal queue
    // is a clean slate, not a refinement of leftover filters. The
    // search box keeps its text.
    updateFilters((f) => ({
      ...f,
      status: null,
      assignee: "all",
      priority: "",
      type: "",
      tag: "",
      deletedOnly: false,
      openOrUnassigned: false,
      mineOrUnassigned: true,
    }));
  }

  // Click a column header to set / toggle the sort. Same column toggles
  // direction (asc <-> desc); a different column switches with a
  // sensible default direction for that data type (desc for date /
  // number / priority - "most recent" / "highest" first; asc for text
  // columns - alphabetical reads better top-down).
  function toggleSort(column: ListSortBy) {
    updateFilters((f) => {
      if (f.sort_by === column) {
        return { ...f, sort_order: f.sort_order === "desc" ? "asc" : "desc" };
      }
      const desc_default: ListSortBy[] = [
        "ticket_number",
        "priority",
        "last_activity_at",
      ];
      return {
        ...f,
        sort_by: column,
        sort_order: desc_default.includes(column) ? "desc" : "asc",
      };
    });
  }

  // Count filter selections that differ from the defaults, so the
  // collapsed mobile Filters dock can signal "N filters active" without
  // the agent expanding it. Defaults: status "open", assignee "all",
  // priority/type/tag empty.
  const activeFilterCount =
    (filters.mineOrUnassigned ||
    filters.openOrUnassigned ||
    filters.deletedOnly ||
    (filters.status ?? "") !== "open"
      ? 1
      : 0) +
    (filters.assignee !== "all" && filters.assignee !== "" ? 1 : 0) +
    (filters.priority !== "" ? 1 : 0) +
    (filters.type !== "" ? 1 : 0) +
    (filters.tag !== "" ? 1 : 0);

  return (
    // Full-width container. Agents work with dense, table-shaped data
    // and asked for the inbox to use the available horizontal real
    // estate instead of a centered 6xl column. The AppShell's <main>
    // already provides p-6 breathing room around the edges.
    // `pb-28` on mobile reserves room for the fixed Filters dock; reset
    // to `lg:pb-0` on desktop where the filter bar sits at the top.
    <div className="w-full pb-28 lg:pb-0">
      {/* Header row: title left, search box right. flex-wrap so the
       * search input drops below the title on narrow viewports rather
       * than crowding the header. */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <i
            className="fa-duotone fa-thin fa-inbox text-3xl text-primary-600"
            aria-hidden="true"
          />
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Tickets</h1>
            <p className="mt-1 text-sm text-neutral-500" data-testid="inbox-subtitle">
              {subtitle}
            </p>
          </div>
        </div>
        <div className="relative w-full shrink-0 sm:w-72">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400">
            <i
              className="fa-duotone fa-thin fa-magnifying-glass text-sm"
              aria-hidden="true"
            />
          </span>
          <input
            id="q-filter"
            type="search"
            placeholder="Search tickets..."
            value={filters.q}
            onChange={(e) =>
              updateFilters((f) => ({ ...f, q: e.target.value }))
            }
            aria-label="Search tickets"
            className="block w-full rounded-lg border-neutral-300 py-2 pl-9 pr-9 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
          {filters.q.length > 0 && (
            <button
              type="button"
              onClick={() => updateFilters((f) => ({ ...f, q: "" }))}
              aria-label="Clear search"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-700"
            >
              <i
                className="fa-duotone fa-thin fa-xmark text-sm"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>

      {/* Filter bar: four dropdowns. Status leads so agents pivot the
       * queue view first, then narrow by Assignee / Priority / Type.
       * Dropdowns are disabled in search mode: a free-text query
       * searches the entire ticket corpus, the filter selections do
       * not narrow that result set (they apply again the moment the
       * search box is cleared). */}
      {/* Filters dock. ONE instance of the filter bar, repositioned by
          CSS: a docked, expandable bottom sheet on mobile (< lg); the
          inline bar at the top on desktop. Not duplicated (the Selects
          carry ids/aria-labels the tests target). z-20 sits under the
          AppShell nav drawer (z-40) / overlay (z-30). */}
      {/* The peek bar was white-on-white against the page and agents
          were missing it (Greg, 2026-07-30). On mobile it now carries
          the primary tint, a heavier top edge, and more height so it
          reads as a control rather than as the end of the list. Every
          bit of that is `max-lg:` / `lg:`-reset so the desktop filter
          bar is untouched. */}
      <div
        data-testid="mobile-filters-dock"
        className="fixed inset-x-0 bottom-0 z-20 border-t-2 border-primary-500 bg-primary-50 shadow-[0_-4px_14px_rgba(0,0,0,0.12)] lg:static lg:z-auto lg:border-0 lg:bg-transparent lg:shadow-none"
      >
        <button
          type="button"
          onClick={() => setFiltersDockOpen((v) => !v)}
          aria-expanded={filtersDockOpen}
          aria-controls="agent-inbox-filters"
          data-testid="mobile-filters-peek"
          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left lg:hidden"
        >
          <span className="flex items-center gap-2 text-base font-semibold text-primary-800">
            <i
              className="fa-duotone fa-thin fa-filter text-lg text-primary-600"
              aria-hidden="true"
            />
            Filters
            {activeFilterCount > 0 && (
              <span
                data-testid="active-filter-count"
                className="inline-flex items-center rounded-full bg-primary-200 px-2 py-0.5 text-xs font-medium text-primary-800"
              >
                {activeFilterCount}
              </span>
            )}
          </span>
          <i
            className={`fa-duotone fa-thin ${
              filtersDockOpen ? "fa-chevron-down" : "fa-chevron-up"
            } text-lg text-primary-600`}
            aria-hidden="true"
          />
        </button>
        <div
          id="agent-inbox-filters"
          className={`${
            filtersDockOpen ? "block" : "hidden"
          } max-h-[70vh] overflow-y-auto px-4 pb-4 lg:block lg:max-h-none lg:overflow-visible lg:p-0`}
        >
      <div
        className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-5"
        title={isSearchMode ? "Filters do not apply while searching." : undefined}
      >
        <Select
          id="status-filter"
          options={STATUS_OPTIONS}
          value={
            filters.mineOrUnassigned
              ? MINE_OR_UNASSIGNED_SENTINEL
              : filters.openOrUnassigned
                ? OPEN_OR_UNASSIGNED_SENTINEL
                : filters.deletedOnly
                  ? DELETED_SENTINEL
                  : filters.status ?? ""
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === MINE_OR_UNASSIGNED_SENTINEL) {
              selectMineOrUnassigned();
            } else if (v === OPEN_OR_UNASSIGNED_SENTINEL) {
              selectOpenOrUnassigned();
            } else if (v === DELETED_SENTINEL) {
              selectDeleted();
            } else {
              selectStatus(v === "" ? null : (v as TicketStatus));
            }
          }}
          aria-label="Status"
          disabled={isSearchMode}
        />
        <Select
          id="assignee-filter"
          options={assigneeOptions}
          value={filters.assignee}
          onChange={(e) => {
            // Defend against picking the visual separator that sits
            // between the static options and the agent directory.
            // Native <select> disables that option visually but a
            // programmatic change could still try.
            if (e.target.value === "__sep__") return;
            updateFilters((f) => ({ ...f, assignee: e.target.value }));
          }}
          aria-label="Assigned to"
          disabled={isSearchMode}
        />
        <Select
          id="priority-filter"
          options={PRIORITY_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          value={filters.priority}
          onChange={(e) =>
            updateFilters((f) => ({
              ...f,
              priority: e.target.value as TicketPriority | "",
            }))
          }
          aria-label="Priority"
          disabled={isSearchMode}
        />
        <Select
          id="type-filter"
          options={TYPE_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
          value={filters.type}
          onChange={(e) =>
            updateFilters((f) => ({
              ...f,
              type: e.target.value as TicketType | "",
            }))
          }
          aria-label="Type"
          disabled={isSearchMode}
        />
        <Select
          id="tag-filter"
          options={tagOptions}
          value={filters.tag}
          onChange={(e) =>
            updateFilters((f) => ({ ...f, tag: e.target.value }))
          }
          aria-label="Tag"
          disabled={isSearchMode}
        />
      </div>
        </div>
      </div>

      {/* Table */}
      {(() => {
        // Pick the source: search hits flatten across infinite pages,
        // otherwise the list endpoint result. The two share the InboxRow
        // shape via fromSummary / fromSearchHit so the table doesn't
        // know which side served it.
        const isLoading = isSearchMode ? search.isLoading : list.isLoading;
        const isError = isSearchMode ? search.isError : list.isError;
        const error = isSearchMode ? search.error : list.error;
        const rows: InboxRow[] = isSearchMode
          ? (search.data?.pages ?? []).flatMap((p) =>
              p.data.map(fromSearchHit),
            )
          : (list.data ?? []).map(fromSummary);
        const hasNextPage = isSearchMode ? search.hasNextPage : false;
        const isFetchingNextPage = isSearchMode
          ? search.isFetchingNextPage
          : false;

        return (
          // On mobile the rows become standalone cards (see
          // styles/index.css .tickets-list-table), so drop the outer
          // box chrome there and let the cards sit on the page; desktop
          // keeps the bordered table container.
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white max-lg:overflow-visible max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent">
            {isLoading && (
              <div className="flex items-center justify-center px-4 py-12">
                <Spinner />
              </div>
            )}

            {isError && (
              <div className="px-4 py-8 text-center text-sm text-red-700">
                {error instanceof Error
                  ? error.message
                  : "Failed to load tickets."}
              </div>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                <i
                  className="fa-duotone fa-thin fa-inbox text-4xl text-neutral-300"
                  aria-hidden="true"
                />
                {isSearchMode ? (
                  <>
                    <p className="mt-2 text-sm font-medium text-neutral-700">
                      No tickets matched &lsquo;{trimmedQ}&rsquo;.
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      Try different terms or clear the search.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="mt-2 text-sm font-medium text-neutral-700">
                      No tickets match this filter
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      Try a different status or clear the search field.
                    </p>
                  </>
                )}
              </div>
            )}

            {!isLoading && !isError && rows.length > 0 && (
              <>
                <table className="tickets-list-table min-w-full divide-y divide-neutral-100">
                  <thead className="bg-neutral-50">
                    <tr className="text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                      <SortableTh column="ticket_number" label="#" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="status" label="Status" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="priority" label="Priority" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="requester_name" label="Contact" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="company_name" label="Company" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="subject" label="Subject" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="assignee_name" label="Assignee" filters={filters} onToggle={toggleSort} />
                      <SortableTh column="last_activity_at" label="Last Activity" filters={filters} onToggle={toggleSort} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {rows.map((t) => (
                      <TicketTableRow key={t.id} ticket={t} />
                    ))}
                  </tbody>
                </table>
                {hasNextPage && (
                  <div className="flex items-center justify-center border-t border-neutral-100 px-4 py-3">
                    <button
                      type="button"
                      onClick={() => {
                        void search.fetchNextPage();
                      }}
                      disabled={isFetchingNextPage}
                      className="text-xs font-medium text-primary-700 hover:text-primary-800 disabled:text-neutral-400"
                    >
                      {isFetchingNextPage ? "Loading..." : "Load more"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      <p className="mt-4 text-xs text-neutral-400">
        Need to file a ticket for a customer?{" "}
        <Link
          to="/tickets/new"
          className="font-medium text-primary-700 hover:text-primary-800"
        >
          New ticket
        </Link>
        .
      </p>
    </div>
  );
}

export default AgentInboxPage;
